import { addBreadcrumb, captureException } from '@sentry/browser'
import { AxiosError } from 'axios'
import { batch } from 'solid-js'
import { isServer } from 'solid-js/web'
import { useAuthPlayerIdSignal } from '../../../../rx/signal/useAuthPlayerIdSignal'
import { useActionLogsSignal } from '../../../../rx/signal/useActionLogsSignal'
import { useEnginePrevStateSignal } from '../../../../rx/signal/useEnginePrevStateSignal'
import { flatten, isValiError, parse } from 'valibot'
import { setAlertBagError } from '../../../../rx/store/AlertBag'
import { deepClone } from '../../../deep_clone'
import { getEngineAlertBag } from '../../../three/fn/getSetEngineAlertBag'
import { getEngineToast } from '../../../three/fn/getSetEngineToast'
import { Engine } from '../../engine/Engine.type'
import { EntityTypeId } from '../../entity/EntityTypeId.enum'
import findEntityById from '../../entity/findEntityById'
import getEntitiesAtPosition from '../../entity/getEntitiesAtPosition'
import type { TilePositionXY } from '../../tile_position_xy/TilePositionXY.type'
import toCoord from '../../tile_position_xy/toCoord'
import { byLayerIdDesc } from '../../util/id'
import { StateSchema } from '../State.type'
import Action from './action/Action.type'
import { ActionType } from './action/ActionType'
import type { MoveUnitAction } from './action/Game/MoveUnitAction'
import getActionTypeText from './action/getActionTypeText'
import type ActionHandler from './ActionHandler.type'
import actionHandlersList, { type ActionSchemaUnion } from './actionHandlersList.generated'
import type { ActionLog } from './ActionLog.type'
import type { ActionUnion } from './ActionUnion.type'
import nextClientReferenceId from './nextClientReferenceId'
import postRemoteAction from './postRemoteAction'

// let lastClientDispatch: Promise<unknown> | null = null
export default async function dispatchClient(engine: Engine, action: Action): Promise<void> {
  const [getAuthPlayerId] = useAuthPlayerIdSignal(engine)
  const [getActionLogs, setActionLogs] = useActionLogsSignal(engine)
  const [getPrevState, setPrevState] = useEnginePrevStateSignal(engine)
  console.log('%c[dispatchClient]', 'color: #BB6; font-weight: lighter; font-size: 10px;', getActionTypeText(action.type), {
    authPlayerId: getAuthPlayerId(),
    // players: engine.state.players.map((p) => ({
    //   player_id: p.id,
    //   profile_id: p.profile_id,
    // })),
    action: deepClone(action),
  })
  if (action.type === ActionType.Game.MoveUnit) {
    const moveUnitAction = action as MoveUnitAction
    const unit = findEntityById(engine.state.ents, moveUnitAction.unit_id)
    if (unit) {
      console.log('%c[dispatchClient][MoveUnit]', 'color: #BB6; font-weight: lighter; font-size: 10px;',
        EntityTypeId[unit.etype_id], toCoord(unit))
    }
  }

  addBreadcrumb({
    category: 'action',
    level: 'info',
    data: {
      action,
    },
  })
  if (isServer) {
    throw new Error('dispatchClient is only available on the client')
  }
  const { state : { round, ended } } = engine
  const authPlayerId = getAuthPlayerId()
  if (ended) {
    console.log('dispatchClient: game ended, ignoring action')
    return
  }
  const actionLogs = getActionLogs()
  const originalActionLogsLen = actionLogs.length
  const actionLog: ActionLog = {
    cref: nextClientReferenceId(actionLogs, authPlayerId, round),
    round,
    player_id: authPlayerId,
    at: Date.now(),
    action: null as unknown as ActionUnion,
  }
  try {
    // const handler = actionHandlersList.get(action.type)
    const tuple = actionHandlersList.find((t) => action.type === t[0])
    if (tuple) {
      // const actionType = tuple[0]
      const actionSchema: ActionSchemaUnion = tuple[1]
      const actionHandler: ActionHandler = tuple[2]
      const validatedAction = parse(actionSchema, action)
      // await actionHandler(engine, validatedAction)

      actionLog.action = validatedAction

      let remoteActionPromise: Promise<unknown> | null = null
      const onlineId = engine.state.online?.id
      if (onlineId) {
        remoteActionPromise = postRemoteAction(engine, validatedAction, onlineId, actionLog)
      }

      // batch is intended for non-async actions
      // async actions will need to do their own batching
      // if necessary. batch is used to avoid re-computing
      // the entire state tree until the action is complete.
      // Some actions (mostly map editor) may rewrite the entire ents list.
      let actionPromise
      batch(() => {
        actionPromise = actionHandler(engine, validatedAction, actionLog)
      })
      await actionPromise

      batch(() => {
        engine.state = parse(StateSchema, engine.state)

        setActionLogs([...actionLogs, actionLog!])
      })
      setPrevState(deepClone(engine.state))

      await remoteActionPromise

      // engine.state.ents.forEach((ent) => {
      //   if (isEntityInfantry(ent)) {
      //     ent.hasError = true
      //   }
      // })
    } else {
      throw new Error(`Action ${action.type} is not supported`)
    }
  } catch (err) {
    const toast = getEngineToast(engine)
    const ab = getEngineAlertBag(engine)
    const msg: string = (((err as AxiosError).response?.data as { message?: string})?.message)
      || (err as { message?: string }).message
      || 'unknown'
    const context: Record<string, unknown> = {
      action,
    }
    if (isValiError(err)) {
      context.errors = flatten(err.issues)
    }
    const err2 = new Error(`Action Failed: ${msg}`, { cause: err })
    console.error(err, context)
    captureException(err, context)
    if (toast) {
      toast.error(msg)
    }
    if (ab) {
      setAlertBagError(ab, err2)
    }

    // restore original state
    engine.state = deepClone(getPrevState())
    setActionLogs(actionLogs.slice(0, originalActionLogsLen))

    if (actionLog) {
      if (action.type === ActionType.Game.MoveUnit) {
        const moveUnitAction = action as MoveUnitAction
        const entIdKeys : Array<keyof MoveUnitAction> = [
          'unit_id',
          'target_id',
          'pickup_id',
          'taxi_id',
          'cargo_id',
          'merge_id',
        ] as const
        entIdKeys.forEach((key) => {
          if (moveUnitAction[key]) {
            const unit = findEntityById(engine.state.ents, moveUnitAction[key] as number)
            if (unit) {
              unit.hasError = true
            }
          }
        })
        const positionKeys : Array<keyof MoveUnitAction> = [
          'start_position',
          'dest_position',
          'attack_position',
          'unload_position',
        ] as const
        positionKeys.forEach((key) => {
          if (moveUnitAction[key]) {
            const entStack = getEntitiesAtPosition(engine.state.ents, moveUnitAction[key] as TilePositionXY)
            entStack.sort(byLayerIdDesc)
            if (entStack[0]) {
              entStack[0].hasError = true
            }
          }
        })
      }
    }
    throw err2
  }
}
